import { getAuthConfig } from "../config/AuthConfigContext";
import { DEFAULT_API_URLS } from "../config/defaults";
import { logDataToConsole, logError } from "../utils/customLogger";
import { getBearerToken } from "./bearerToken";

export interface MagicLinkSignUpPayload {
  email: string;
  env: "production" | "staging";
  action_code_url: string;
}

export interface MagicLinkSignUpResponse {
  success: boolean;
  message?: string;
  data?: unknown;
}

export const makeMagicLinkSignUpCall = async (
  payload: MagicLinkSignUpPayload,
): Promise<MagicLinkSignUpResponse> => {
  try {
    logDataToConsole("Making magic link signup call with payload", payload);

    const token = await getBearerToken();

    if (!token) {
      throw new Error("Bearer token not available");
    }

    const config = getAuthConfig();
    const authUrl = config?.apiUrls?.authUrl ?? DEFAULT_API_URLS.AUTH_URL;

    const response = await fetch(`${authUrl}/magic-link-signup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError(
        new Error(
          `Magic link signup failed: ${response.status} ${response.statusText}`,
        ),
        errorText,
      );
      return {
        success: false,
        message: `Request failed with status ${response.status}: ${response.statusText}`,
      };
    }

    const responseData = await response.json();
    logDataToConsole("Magic link signup successful", responseData);

    return {
      success: true,
      data: responseData,
    };
  } catch (error) {
    logError(error as Error, "Error in makeMagicLinkSignUpCall");
    return {
      success: false,
      message:
        error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
};

export const sendMagicLinkSignUp = async (
  email: string,
  env: "production" | "staging" = "production",
): Promise<MagicLinkSignUpResponse> => {
  const payload: MagicLinkSignUpPayload = {
    email,
    env,
    action_code_url: `${window.location.origin}/finishSignIn`,
  };

  return makeMagicLinkSignUpCall(payload);
};
